import { useState, useCallback, useMemo } from "react";
import {
  MultiSelect,
  MultiSelectChangeEvent,
  MultiSelectFilterChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { filterBy, FilterDescriptor } from "@progress/kendo-data-query";
import { useMultiSelectChipListLabel } from "@/components/shared/forms/useMultiSelectChipListLabel";
import { useMultiSelectListboxMultiselectable } from "@/components/shared/forms/useMultiSelectListboxMultiselectable";
import { multiSelectSelectedItemRender } from "@/components/shared/forms/multiSelectSelectedItemRender";
import { MultiSelectToggleButton } from "@/components/shared/forms/MultiSelectToggleButton";
import { MultiSelectClearButton } from "@/components/shared/forms/MultiSelectClearButton";
import { multiSelectTagRender } from "@/components/shared/forms/multiSelectTagRender";

export type LanguageOption = {
  Text: string;
  Value: string;
};

type LanguageMultiSelectProps = {
  data: LanguageOption[];
  value: LanguageOption[];
  onChange: (items: LanguageOption[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  required?: boolean;
  invalid?: boolean;
};

export function LanguageMultiSelect({
  data,
  value,
  onChange,
  disabled = false,
  placeholder,
  className,
  id,
  ariaLabel,
  ariaDescribedBy,
  required,
  invalid,
}: LanguageMultiSelectProps) {
  const [filter, setFilter] = useState<string>("");
  // Kendo's MultiSelect has no visible dropdown-arrow button by default (unlike
  // DropDownList/ComboBox), so there's no way to browse the full option list
  // without typing something first - keyboard users have no discoverable way in
  // (the only keyboard shortcut that opens it, Alt+Down, isn't documented on
  // screen anywhere). This adds a real, focusable button that does the same job.
  const [opened, setOpened] = useState(false);

  const filteredData = useMemo(() => {
    if (!filter) return data;
    const descriptor: FilterDescriptor = {
      field: "Text",
      operator: "contains",
      value: filter,
      ignoreCase: true,
    };
    return filterBy(data, descriptor);
  }, [data, filter]);

  const handleFilterChange = useCallback((e: MultiSelectFilterChangeEvent) => {
    setFilter(e.filter.value ?? "");
  }, []);

  const handleChange = useCallback(
    (e: MultiSelectChangeEvent) => {
      onChange(e.value as LanguageOption[]);
    },
    [onChange]
  );

  const handleClose = useCallback(() => {
    setFilter("");
    setOpened(false);
  }, []);

  const handleOpen = useCallback(() => {
    setOpened(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFilter("");
  }, []);

  useMultiSelectChipListLabel(id, ariaLabel, value);
  useMultiSelectListboxMultiselectable(id, opened);

  const noDataRender = () => (
    <div className="k-nodata">
      <div>No languages found.</div>
    </div>
  );

  return (
    <MultiSelect
      id={id}
      ariaLabel={ariaLabel}
      ariaDescribedBy={ariaDescribedBy}
      data={filteredData}
      textField="Text"
      dataItemKey="Value"
      value={value}
      onChange={handleChange}
      disabled={disabled}
      required={required}
      valid={!invalid}
      inputAttributes={invalid ? { "aria-invalid": true } : undefined}
      placeholder={placeholder}
      className={className}
      filterable={true}
      filter={filter}
      onFilterChange={handleFilterChange}
      opened={opened}
      onOpen={handleOpen}
      onClose={handleClose}
      onBlur={handleBlur}
      allowCustom={false}
      listNoDataRender={noDataRender}
      itemRender={multiSelectSelectedItemRender}
      tagRender={multiSelectTagRender}
      suffix={
        <>
          <MultiSelectToggleButton
            opened={opened}
            onToggle={() => setOpened((prev) => !prev)}
            disabled={disabled}
          />
          {value.length > 0 && (
            <MultiSelectClearButton
              onClear={() => onChange([])}
              disabled={disabled}
            />
          )}
        </>
      }
      popupSettings={{
        className: `language-multiselect-popup${id ? ` ${id}-listbox-popup` : ""}`,
        height: "auto",
      }}
      style={{ width: "100%" }}
    />
  );
}
